import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TeamShipHealth } from './battleship-quiz';
import { TeamScoreboard } from './team-scoreboard';

import './styles/end-screen.css';
import { useEventContext, useQuizContext, useQuizSessionContext } from 'contexts';
import { NavUtils, NavPages } from 'utils';
import { BattleshipGameTypes, BattleshipUserSessionAdditionalInfo } from './types';
import { AnsweredQuestion } from 'types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EnhancedTable, TableHeadCell } from 'components';

interface AnsweredQuestionRow {
    id: number;
    question?: string;
    correctAnswer: string;
    userAnswer: string;
    answeredCorrectly: boolean;
}

interface Props {
    gameType: BattleshipGameTypes;
    correctAnswers: number;
    incorrectAnswers: number;
    teamShipStatus: TeamShipHealth[];
    answeredQuestions: AnsweredQuestion[];
}

export const BattleshipEndScreen = (props: Props) => {
    const { quiz } = useQuizContext();
    const sessionContext = useQuizSessionContext();
    const event = useEventContext();
    const navigate = useNavigate();

    const getWinningTeam = () => {
        const winner = props.teamShipStatus.find((t) => t.shipCount - t.destroyed > 0);
        return winner?.name;
    };

    const onQuizListClick = () => {
        navigate(NavUtils.generateNavPath(NavPages.QuizSelector, undefined, event?.alias));
    };

    const getShipStatuses = (): TeamShipHealth[] => {
        const lTeamShipStatus = [...props.teamShipStatus];
        if (sessionContext) {
            const shipStatuses = (sessionContext?.userSession?.additionalInfo as BattleshipUserSessionAdditionalInfo)?.shipStatus;
            if (shipStatuses) {
                shipStatuses.forEach((ss) => {
                    const cTeam = lTeamShipStatus.find((t) => t.name === ss.teamName)!;
                    cTeam.destroyed = ss.sunkShips;
                });
            }
        }

        return lTeamShipStatus;
    };

    const correctAnswers = sessionContext?.userSession?.correctAnswers ?? props.correctAnswers;
    const incorrectAnswers = sessionContext?.userSession?.answersAttempted
        ? sessionContext?.userSession?.answersAttempted - correctAnswers
        : props.incorrectAnswers;

    // sort answered questions alphabetically
    const orderedQuestions: AnsweredQuestionRow[] = props.answeredQuestions
        .sort((a, b) => (a.question ?? '').localeCompare(b.question ?? ''))
        .map(a => ({
            id: a.id!,
            question: a.question,
            userAnswer: a.userAnswer!,
            answeredCorrectly: a.answeredCorrectly,
            correctAnswer: a.answers.find(ans => ans.isCorrect)?.answer!
        }));
    const tableHeaderCells: TableHeadCell<AnsweredQuestionRow>[] = [
        {
            id: 'question',
            label: 'Question'
        },
        {
            id: 'userAnswer',
            label: 'Your Answer'
        },
        {
            id: 'answeredCorrectly',
            label: 'Is Correct'
        },
        {
            id: 'correctAnswer',
            label: 'Correct Answer'
        }
    ];

    return (
        <Box className="end-screen-wrapper">
            <Paper variant="elevation" elevation={3}>
                <Stack className="end-screen-stack" direction="column" spacing={2}>
                    <h2 className="quiz-title">{quiz.name}</h2>
                    <Stack direction="row" spacing={2} padding="0 30px">
                        <Typography variant="body1">Total correctly answered questions:</Typography>
                        <Typography variant="body1">{correctAnswers}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} padding="0 30px">
                        <Typography variant="body1">Total incorrectly answered questions:</Typography>
                        <Typography variant="body1">{incorrectAnswers}</Typography>
                    </Stack>
                    {props.gameType === BattleshipGameTypes.LIVE_MULTI_TEAM && (
                        <Stack direction="row" spacing={2} padding="0 30px">
                            <Typography variant="h3">WINNER:</Typography>
                            <Typography variant="h3">{getWinningTeam()}</Typography>
                        </Stack>
                    )}
                    <TeamScoreboard teamShipStatus={getShipStatuses()} />
                    <Button onClick={onQuizListClick}> Back to Quiz List</Button>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="questions-accordion-content"
                            id="questions-accordion-header"
                        >
                            <Typography>Question Overview</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <EnhancedTable<AnsweredQuestionRow>
                                title="Quiz questions"
                                data={orderedQuestions}
                                headerCells={tableHeaderCells}
                                dataRecordIdKey="id"
                                defaultRowsPerPage={10}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Paper>
        </Box>
    );
};
